import type {
  FunnelMomentStageCount,
  FunnelOutcomeExitCount,
  FunnelStageCounts,
} from "@repo/app-types/api/funnel";
import type {
  FunnelMoments,
  FunnelStages,
} from "@repo/app-types/entities/funnel";
import type { OutcomeGreenRedYellowResultCategory } from "@repo/app-types/entities/outcome";

const shareFormat = new Intl.NumberFormat("en-US", {
  style: "percent",
  maximumFractionDigits: 0,
});

/** A share in 0..1 as a whole percentage, or a dash. */
export function formatFunnelShare(share: number | null): string {
  return share === null ? "–" : shareFormat.format(share);
}

const preciseShareFormat = new Intl.NumberFormat("en-US", {
  style: "percent",
  maximumFractionDigits: 1,
});

/** A share in 0..1 as a percentage to one decimal. */
export function formatFunnelPreciseShare(share: number): string {
  return preciseShareFormat.format(share);
}

/** The name of the stage an exchange happened in; null is before any stage. */
export function formatFunnelStageName(
  stages: FunnelStages,
  stageKey: string | null,
): string {
  if (stageKey === null) {
    const firstStage = stages[0];
    return firstStage ? `Before ${firstStage.name}` : "Before any stage";
  }
  return stages.find((stage) => stage.key === stageKey)?.name ?? stageKey;
}

/** A stage's interactions in prose: "Offer interactions", "interactions before Greeting". */
export function formatFunnelStageInteractions(
  stages: FunnelStages,
  stageKey: string | null,
): string {
  if (stageKey === null) {
    const firstStage = stages[0];
    return `interactions before ${firstStage ? firstStage.name : "any stage"}`;
  }
  return `${formatFunnelStageName(stages, stageKey)} interactions`;
}

/** Interactions that reached a stage; before the first stage, all of them. */
function findFunnelStageInteractions(
  stageCounts: FunnelStageCounts,
  stageKey: string | null,
): number {
  if (stageKey === null) {
    return stageCounts.denominator;
  }
  return (
    stageCounts.stages.find((stage) => stage.stage_key === stageKey)?.reached ??
    0
  );
}

/** The interactions that went no further than a stage, by outcome; null is before the first stage. */
export function findFunnelStageExitsByOutcome(
  stageCounts: FunnelStageCounts,
  stageKey: string | null,
): FunnelOutcomeExitCount[] {
  if (stageKey === null) {
    return stageCounts.reached_no_stage_by_outcome;
  }
  return (
    stageCounts.stages.find((stage) => stage.stage_key === stageKey)
      ?.exited_by_outcome ?? []
  );
}

/** An outcome result's label, or "No outcome" for interactions without one. */
export function formatFunnelOutcomeLabel(label: string | null): string {
  return label ?? "No outcome";
}

/**
 * A color the flow and the bars draw in: continued, an outcome result's
 * category, or without an outcome.
 */
export type FunnelColor =
  | "continued"
  | OutcomeGreenRedYellowResultCategory
  | "none";

/** Each color's classes, written out so Tailwind sees them. */
export const funnelColorClassNames: Record<
  FunnelColor,
  { background: string; fill: string; stroke: string }
> = {
  continued: {
    background: "bg-indigo-400",
    fill: "fill-indigo-400",
    stroke: "stroke-indigo-400",
  },
  green: {
    background: "bg-green-400",
    fill: "fill-green-400",
    stroke: "stroke-green-400",
  },
  red: {
    background: "bg-red-400",
    fill: "fill-red-400",
    stroke: "stroke-red-400",
  },
  yellow: {
    background: "bg-yellow-400",
    fill: "fill-yellow-400",
    stroke: "stroke-yellow-400",
  },
  gray: {
    background: "bg-gray-400",
    fill: "fill-gray-400",
    stroke: "stroke-gray-400",
  },
  none: {
    background: "bg-gray-300",
    fill: "fill-gray-300",
    stroke: "stroke-gray-300",
  },
};

/** The color an outcome category is drawn in; no outcome is a lighter gray. */
export function findFunnelOutcomeColor(
  category: OutcomeGreenRedYellowResultCategory | null,
): FunnelColor {
  return category ?? "none";
}

/** Exits are drawn green, red, yellow, gray, then without an outcome. */
const funnelOutcomeCategoryOrder: readonly OutcomeGreenRedYellowResultCategory[] =
  ["green", "red", "yellow", "gray"];

function rankFunnelOutcomeCategory(
  category: OutcomeGreenRedYellowResultCategory | null,
): number {
  return category === null
    ? funnelOutcomeCategoryOrder.length
    : funnelOutcomeCategoryOrder.indexOf(category);
}

/** Exits in category order, the largest first within a category. */
export function sortFunnelExitsByCategory(
  exits: readonly FunnelOutcomeExitCount[],
): FunnelOutcomeExitCount[] {
  return [...exits].sort(
    (a, b) =>
      rankFunnelOutcomeCategory(a.category) -
        rankFunnelOutcomeCategory(b.category) ||
      b.interactions - a.interactions,
  );
}

export const FunnelFlowNodeKind = {
  Start: "start",
  Stage: "stage",
  Exit: "exit",
} as const;

export type FunnelFlowNodeKind =
  (typeof FunnelFlowNodeKind)[keyof typeof FunnelFlowNodeKind];

export interface FunnelFlowNode {
  kind: FunnelFlowNodeKind;
  name: string;
  /** Interactions. */
  value: number;
  /** The stage selecting this node shows; null is before the first stage. */
  stageKey: string | null;
  color: FunnelColor;
}

export interface FunnelFlowLink {
  source: number;
  target: number;
  /** Interactions. */
  value: number;
  isExit: boolean;
  /** The source node's stage. */
  stageKey: string | null;
  /** The target node's. */
  color: FunnelColor;
}

export interface FunnelFlowLegendEntry {
  label: string;
  color: FunnelColor;
}

export interface FunnelFlowData {
  nodes: FunnelFlowNode[];
  links: FunnelFlowLink[];
  /** Continued, the success stage when the last one is, then the exits' outcomes. */
  legend: FunnelFlowLegendEntry[];
}

/**
 * The Sankey of one cohort's interactions: a start node, then each stage, each
 * but the last with an exit per outcome for the interactions that went no
 * further, below the link to the next stage in category order. A last stage
 * that is the outcome's success result is drawn in the success color. Links
 * worth nothing are left out, and so are the nodes that leaves unconnected.
 */
export function buildFunnelFlowData(
  stages: FunnelStages,
  stageCounts: FunnelStageCounts,
): FunnelFlowData {
  const countByKey = new Map(
    stageCounts.stages.map((stageCount) => [stageCount.stage_key, stageCount]),
  );
  const successColor = findFunnelOutcomeColor("green");
  const stageNodes = stages.map((stage): FunnelFlowNode => ({
    kind: FunnelFlowNodeKind.Stage,
    name: stage.name,
    value: countByKey.get(stage.key)?.reached ?? 0,
    stageKey: stage.key,
    color: stage.is_outcome_success ? successColor : "continued",
  }));
  const startNode: FunnelFlowNode = {
    kind: FunnelFlowNodeKind.Start,
    name: "All interactions",
    value: stageCounts.denominator,
    stageKey: null,
    color: "continued",
  };
  // The start node, then one per stage.
  const flowNodes = [startNode, ...stageNodes];

  const nodes: FunnelFlowNode[] = [...flowNodes];
  const links: FunnelFlowLink[] = [];
  const exitLegend = new Map<
    string,
    FunnelFlowLegendEntry & { categoryRank: number }
  >();
  flowNodes.forEach((node, index) => {
    const nextNode = flowNodes[index + 1];
    if (!nextNode) {
      return;
    }
    links.push({
      source: index,
      target: index + 1,
      value: nextNode.value,
      isExit: false,
      stageKey: node.stageKey,
      color: nextNode.color,
    });
    for (const exit of sortFunnelExitsByCategory(
      findFunnelStageExitsByOutcome(stageCounts, node.stageKey),
    )) {
      const label = formatFunnelOutcomeLabel(exit.label);
      const color = findFunnelOutcomeColor(exit.category);
      exitLegend.set(`${exit.category ?? ""}:${label}`, {
        label,
        color,
        categoryRank: rankFunnelOutcomeCategory(exit.category),
      });
      nodes.push({
        kind: FunnelFlowNodeKind.Exit,
        name: label,
        value: exit.interactions,
        stageKey: node.stageKey,
        color,
      });
      links.push({
        source: index,
        target: nodes.length - 1,
        value: exit.interactions,
        isExit: true,
        stageKey: node.stageKey,
        color,
      });
    }
  });

  const nonZeroLinks = links.filter((link) => link.value > 0);
  const connected = new Set(
    nonZeroLinks.flatMap((link) => [link.source, link.target]),
  );
  const newIndexByOldIndex = new Map<number, number>();
  const connectedNodes = nodes.filter((_node, index) => {
    if (!connected.has(index)) {
      return false;
    }
    newIndexByOldIndex.set(index, newIndexByOldIndex.size);
    return true;
  });
  const lastStage = stages.at(-1);
  const fixedLegend: FunnelFlowLegendEntry[] = [
    { label: "Continued", color: "continued" },
    ...(lastStage?.is_outcome_success
      ? [{ label: lastStage.name, color: successColor }]
      : []),
  ];
  return {
    nodes: connectedNodes,
    links: nonZeroLinks.map((link) => ({
      ...link,
      source: newIndexByOldIndex.get(link.source) ?? link.source,
      target: newIndexByOldIndex.get(link.target) ?? link.target,
    })),
    legend: [
      ...fixedLegend,
      ...[...exitLegend.values()]
        .sort((a, b) => a.categoryRank - b.categoryRank)
        .map(({ label, color }) => ({ label, color })),
    ],
  };
}

/** One moment in the selected stage, for the agent cohort. */
export interface FunnelStageMomentRow {
  momentKey: string;
  momentName: string;
  momentDescription: string;
  interactions: number;
  continued: number;
  droppedOff: number;
  droppedOffByOutcome: FunnelOutcomeExitCount[];
  /** Interactions that reached the stage. */
  stageInteractions: number;
  /** `interactions` over `stageInteractions`. */
  share: number;
  /** `droppedOff` over `interactions`; null in the last stage, where every interaction ends. */
  dropOffRate: number | null;
}

/** The moments that came up in a stage, the most dropped off first. */
export function buildFunnelStageMomentRows(input: {
  stages: FunnelStages;
  moments: FunnelMoments;
  momentCounts: readonly FunnelMomentStageCount[];
  stageCounts: FunnelStageCounts;
  /** Null is before the first stage. */
  stageKey: string | null;
}): FunnelStageMomentRow[] {
  const { stages, moments, momentCounts, stageCounts, stageKey } = input;
  const stageInteractions = findFunnelStageInteractions(stageCounts, stageKey);
  const isLastStage = stageKey !== null && stages.at(-1)?.key === stageKey;
  const countByMomentKey = new Map(
    momentCounts
      .filter((count) => count.stage_key === stageKey)
      .map((count) => [count.moment_key, count]),
  );

  return moments
    .flatMap((moment): FunnelStageMomentRow[] => {
      const count = countByMomentKey.get(moment.key);
      if (!count || count.interactions === 0 || stageInteractions === 0) {
        return [];
      }
      return [
        {
          momentKey: moment.key,
          momentName: moment.name,
          momentDescription: moment.description,
          interactions: count.interactions,
          continued: count.continued,
          droppedOff: count.dropped_off,
          droppedOffByOutcome: count.dropped_off_by_outcome,
          stageInteractions,
          share: count.interactions / stageInteractions,
          dropOffRate: isLastStage
            ? null
            : count.dropped_off / count.interactions,
        },
      ];
    })
    .sort(
      (a, b) => b.droppedOff - a.droppedOff || b.interactions - a.interactions,
    );
}

/** A cohort's share of a stage's interactions with a moment; null without any. */
export function computeFunnelMomentShare(input: {
  momentCounts: readonly FunnelMomentStageCount[];
  stageCounts: FunnelStageCounts;
  momentKey: string;
  stageKey: string | null;
}): number | null {
  const { momentCounts, stageCounts, momentKey, stageKey } = input;
  const count = momentCounts.find(
    (candidate) =>
      candidate.moment_key === momentKey && candidate.stage_key === stageKey,
  );
  const stageInteractions = findFunnelStageInteractions(stageCounts, stageKey);
  if (!count || count.interactions === 0 || stageInteractions === 0) {
    return null;
  }
  return count.interactions / stageInteractions;
}
